const destinations = [
  {
    icon: "ri-ship-line",
    title: "The Bahamas & Caribbean",
    description:
      "The Bahamas are accessible from Cape Coral both by air through RSW and Punta Gorda, and by water for serious boaters making the Gulf Stream crossing. Caribbean cruises from Port Tampa Bay, Port Everglades, and Port of Miami are enormously popular among Cape Coral retirees and families. Eastern Caribbean itineraries hit St. Thomas, San Juan, and the Bahamas; Western Caribbean stops in Cozumel, Grand Cayman, and Jamaica; Southern Caribbean reaches Aruba, Curacao, and Barbados. The six-month validity rule applies to all cruise passengers — Cape Coral's high volume of first-time cruisers means we see a lot of passport emergencies from this destination category.",
    tags: ["Cruises", "Boating", "Spring Break"],
  },
  {
    icon: "ri-map-pin-2-line",
    title: "Canada",
    description:
      "Canadian travel is a cornerstone of Cape Coral's snowbird lifestyle. Many residents maintain dual-season lives — Cape Coral from November through April, Ontario, Michigan, or New York for the summer. Direct seasonal flights from RSW to Toronto and Montreal make the transition easier than ever. Family visits to grandchildren in Ottawa, Vancouver, or Calgary are common. And the weak Canadian dollar has made cross-border shopping and visiting increasingly attractive. The six-month validity rule for Canada is less strict than for other countries, but a valid passport is still required for all air travel.",
    tags: ["Snowbird Returns", "Family Visits", "Summer Travel"],
  },
  {
    icon: "ri-landscape-line",
    title: "Europe",
    description:
      "European destinations draw Cape Coral's growing retiree demographic for river cruises along the Rhine and Danube, cultural tours of London, Paris, and Rome, and Mediterranean cruises from Rome, Barcelona, and Venice. The Schengen three-month validity rule catches many retirees who assume their passport is fine because it has not technically expired. Cape Coral's relatively affordable cost of living compared to Naples or Sarasota means residents often have more disposable income for international travel than their coastal neighbors — making these high-value, non-refundable trips even more painful when a passport issue arises.",
    tags: ["River Cruises", "Cultural Tours", "Retiree Travel"],
  },
  {
    icon: "ri-treasure-map-line",
    title: "United Kingdom & Ireland",
    description:
      "The UK and Ireland appeal to Cape Coral's heritage travelers, golf enthusiasts, and those seeking English-speaking international destinations. Golf trips to Scotland's St. Andrews and Ireland's championship links courses are popular among the retiree demographic. London city breaks attract younger families and couples. The six-month validity requirement is strict for UK entry, and we see more passport emergencies from UK-bound Cape Coral travelers than almost any other European destination. The weak pound has made Britain increasingly budget-friendly for cost-conscious travelers who might otherwise skip international trips.",
    tags: ["Heritage", "Golf", "Budget Travel"],
  },
  {
    icon: "ri-sun-line",
    title: "Mexico & Costa Rica",
    description:
      "Cancun, Riviera Maya, Puerto Vallarta, and Cabo San Lucas are top Mexican destinations for Cape Coral families, retirees, and spring break travelers. Allegiant's budget flights from Punta Gorda Airport make Cancun accessible at prices that compete with domestic beach vacations. Costa Rica's eco-tourism — zip-lining, volcano hiking, rainforest canopy tours — appeals to Cape Coral's active outdoor families. The Mexico six-month validity rule applies to all passport holders, and the child passport trap hits particularly hard during spring break when parents book trips months in advance but forget to check their children's document expiration dates.",
    tags: ["All-Inclusive", "Eco-Tourism", "Budget Flights"],
  },
  {
    icon: "ri-compass-3-line",
    title: "Panama Canal & Central America",
    description:
      "Panama Canal full-transit cruises, Costa Rica eco-tours, and Belize diving trips appeal to Cape Coral's active retiree and boating communities. Panama Canal cruises are a bucket-list item for many affluent retirees, booking 12–18 months in advance through major cruise lines. Belize's barrier reef — the second largest in the world — draws diving enthusiasts who compare it to their own local Gulf waters. These trips often book well in advance, creating a long window during which passports can expire unnoticed. The multi-country nature of Central American itineraries also means multiple entry requirements to track.",
    tags: ["Bucket List", "Diving", "Cruises"],
  },
];

const stats = [
  { value: "55%", label: "Caribbean cruise or Mexico spring break requests" },
  { value: "RSW", label: "Primary airport — 20–25 min from Cape Coral" },
  { value: "Nov–Apr", label: "Peak snowbird & cruise travel season" },
  { value: "24 hr", label: "Shortest emergency turnaround handled" },
];

export default function CapeCoralDestinationsSection() {
  return (
    <section id="destinations" className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-[#B22222] text-xs font-bold uppercase tracking-[0.15em] mb-3">
            Popular Destinations
          </span>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Cape Coral Residents Travel
          </h2>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            From Caribbean cruises booked through Allegiant at PGD to European river cruises and Bahamas boating trips — here is where Cape Coral travelers are heading.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {destinations.map((d, i) => (
            <div
              key={i}
              className="bg-[#F8F9FB] rounded-lg p-6 border border-gray-100 hover:border-[#F6C344]/50 transition-all hover:shadow-md"
            >
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center mb-4">
                <i className={`${d.icon} text-[#F6C344] text-lg`}></i>
              </div>
              <h3 className="font-serif text-base font-bold text-[#1A365D] mb-3">{d.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">{d.description}</p>
              <div className="flex flex-wrap gap-2">
                {d.tags.map((tag, j) => (
                  <span
                    key={j}
                    className="inline-block bg-white text-[#1A365D] text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border border-gray-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Stats bar */}
        <div className="bg-[#1A365D] rounded-lg p-6 md:p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="text-[#F6C344] font-bold text-2xl md:text-3xl mb-1">{s.value}</div>
                <div className="text-white/70 text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}